'use client'

import type { BarProps } from 'recharts/types/cartesian/Bar'

import { useMemo } from 'react'
import {
  Bar,
  BarChart,
  Cell,
  Customized,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { ChartTooltip } from '@/components/atomic/chart-tooltip'
import Icon from '@/components/atomic/icon'
import { IconName } from '@/components/atomic/icon/types'
import { formatPercentage } from '@/utils/presentation/formatting'

import { NoDataText, XAxisTickText } from './styles'

const DEFAULT_BAR_SIZE = 24

export interface BarConfig {
  key: string
  fill: string
  stackId?: string
  radius?: [number, number, number, number]
  violationColor?: string
}

interface TargetBarChartProps<T> {
  data: T[]
  xAxisKey: string
  bars: BarConfig[]
  tooltipFormatter?: (dataPoint: T) => React.ReactNode
  minValue?: number
  maxValue?: number
  targetValue?: number
  xAxisLabel?: string
  tooltipLabelPrefix?: string
  barSize?: number
}

interface TooltipPayloadItem {
  name: string
  value: number
  fill: string
}

interface CustomTooltipProps {
  active?: boolean
  payload?: TooltipPayloadItem[]
  label?: string
  labelPrefix?: string
}

interface CustomBarProps {
  x?: number
  y?: number
  width?: number
  height?: number
  value?: number
  fill?: string
  minValue?: number
  maxValue?: number
  radius?: [number, number, number, number]
  violationColor?: string
}

const CustomBar = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  value = 0,
  fill,
  minValue,
  maxValue,
  radius,
  violationColor,
}: CustomBarProps) => {
  const isViolation =
    minValue !== undefined &&
    maxValue !== undefined &&
    (value < minValue || value > maxValue)

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={isViolation ? violationColor || 'var(--destructive)' : fill}
        rx={radius?.[0] || 0}
        ry={radius?.[0] || 0}
      />
      {isViolation && (
        <foreignObject x={x + width / 2 - 8} y={y - 20} width={16} height={16}>
          <div className="h-4 w-4">
            <Icon
              iconName={IconName.ExclamationTriangleIcon}
              className="h-4 w-4 text-moment-red-700"
            />
          </div>
        </foreignObject>
      )}
    </g>
  )
}

const BarChartNoData = () => {
  return <NoDataText>No data available</NoDataText>
}

// biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
const CustomXAxisTick = ({ x, y, payload }: any) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <XAxisTickText>{payload.value}</XAxisTickText>
    </g>
  )
}

const CustomTooltip = ({
  active,
  payload,
  label,
  labelPrefix,
}: CustomTooltipProps) => {
  if (!active || !payload?.length) {
    return null
  }

  // Transform payload data to the format expected by ChartTooltip
  const tooltipValues = payload.map((entry) => ({
    color: 'var(--color-moment-teal-700)',
    label: 'Weight',
    value: formatPercentage(entry.value, { numDigits: 2 }),
  }))

  const formattedDate = [labelPrefix, label].filter(Boolean).join(' ')

  return <ChartTooltip date={formattedDate} values={tooltipValues} />
}

export function TargetBarChart<T>({
  data,
  xAxisKey,
  bars,
  tooltipFormatter,
  minValue,
  maxValue,
  targetValue,
  xAxisLabel = '',
  tooltipLabelPrefix = 'Year',
  barSize = DEFAULT_BAR_SIZE,
}: TargetBarChartProps<T>) {
  const hasNoData = useMemo(() => data.length === 0, [data])

  const formattedMaxValue = useMemo(
    () => maxValue && formatPercentage(maxValue, { numDigits: 2 }),
    [maxValue],
  )

  const formattedMinValue = useMemo(
    () => minValue && formatPercentage(minValue, { numDigits: 2 }),
    [minValue],
  )

  const formattedTargetValue = useMemo(
    () => targetValue && formatPercentage(targetValue, { numDigits: 2 }),
    [targetValue],
  )

  const tooltipContent = useMemo(
    () =>
      tooltipFormatter
        ? // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
          (props: any) => {
            if (!props.payload || props.payload.length === 0) return null
            return tooltipFormatter(props.payload[0].payload as T)
          }
        : // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
          (props: any) => (
            <CustomTooltip {...props} labelPrefix={tooltipLabelPrefix} />
          ),
    [tooltipFormatter, tooltipLabelPrefix],
  )

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={{ top: 20, right: 80, left: 15, bottom: 20 }}
        maxBarSize={barSize}
      >
        {hasNoData ? (
          <Customized component={<BarChartNoData />} />
        ) : (
          <>
            <XAxis
              dataKey={xAxisKey}
              axisLine={false}
              tickLine={false}
              scale="auto"
              tick={<CustomXAxisTick />}
              label={{
                value: xAxisLabel,
                position: 'bottom',
                offsetY: 20,
                fontSize: 9,
              }}
            />
            <YAxis
              axisLine={false}
              hide
              domain={[0, maxValue ? maxValue + 1 : 'auto']}
              ticks={[minValue, targetValue, maxValue].filter(
                (v): v is number => typeof v === 'number',
              )}
            />
            <Tooltip
              content={tooltipContent}
              cursor={false}
              animationDuration={50}
            />

            {/* Background colors */}
            {minValue !== undefined && maxValue !== undefined && (
              <ReferenceArea
                y1={minValue}
                y2={maxValue}
                fill="var(--reference-area-background)"
                fillOpacity={0.2}
              />
            )}

            {/* Reference lines */}
            {maxValue !== undefined && (
              <ReferenceLine
                y={maxValue}
                stroke="var(--reference-line-color)"
                strokeDasharray="3 3"
                label={{
                  value: `Max ${formattedMaxValue}`,
                  position: 'right',
                  fill: 'var(--reference-line-color)',
                  fontSize: 9,
                  offset: 10,
                }}
              />
            )}

            {targetValue !== undefined && (
              <ReferenceLine
                y={targetValue}
                stroke="var(--reference-line-target-color)"
                strokeDasharray="3 3"
                label={{
                  value: `Target ${formattedTargetValue}`,
                  position: 'right',
                  fill: 'var(--reference-line-target-color)',
                  offset: 10,
                  fontSize: 9,
                }}
              />
            )}

            {minValue !== undefined && (
              <ReferenceLine
                y={minValue}
                stroke="var(--reference-line-color)"
                strokeDasharray="3 3"
                label={{
                  value: `Min ${formattedMinValue}`,
                  position: 'right',
                  fill: 'var(--reference-line-color)',
                  fontSize: 9,
                  offset: 10,
                }}
              />
            )}

            {bars.map((bar) => (
              <Bar
                key={bar.key}
                dataKey={bar.key}
                stackId={bar.stackId}
                radius={bar.radius}
                shape={(props: BarProps & { fill?: string }) => (
                  <CustomBar
                    {...props}
                    fill={props.fill}
                    minValue={minValue}
                    maxValue={maxValue}
                    radius={bar.radius}
                    violationColor={bar.violationColor}
                  />
                )}
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={bar.fill} />
                ))}
              </Bar>
            ))}
          </>
        )}
      </BarChart>
    </ResponsiveContainer>
  )
}
